/**
 * Rappel de fermeture de caisse — LOGIQUE PURE (testable, sans React).
 *
 * Réutilise l'UNIQUE source d'horaires de l'app (HoursLive.OPENING_HOURS, fuseau America/Cayenne
 * via cayenneNow). L'app ne configure PAS d'horaires par magasin ni d'exceptions/jours fériés :
 * on s'appuie donc sur cet horaire GLOBAL. Un jour normalement fermé (dimanche → OPENING_HOURS[0]=null)
 * est traité comme « après fermeture » dès qu'une caisse est ouverte.
 */
import { OPENING_HOURS, cayenneNow } from '../../components/landing/HoursLive';

export const SNOOZE_PREFIX = 'caisse_reminder_snooze_until::';

// Fournisseur d'heure Cayenne surchargeable UNIQUEMENT pour les tests (données synthétiques).
let _nowProvider = cayenneNow;
export function setNowProviderForTest(fn) { _nowProvider = fn || cayenneNow; }
export function currentCayenne() { return _nowProvider(); }

/**
 * Horaires EFFECTIFS d'un magasin pour un jour donné — ÉTEND la source unique (OPENING_HOURS),
 * sans système parallèle ni jours fériés inventés.
 * Priorité : exception par DATE (magasin.horaires_exceptions[ymd]) > horaire hebdo PAR MAGASIN
 * (magasin.horaires[dow]) > OPENING_HOURS[dow] (défaut GLOBAL existant).
 * Ces champs par magasin sont OPTIONNELS et viennent de la config du magasin ; absents => défaut global.
 * Valeur `null` = fermé ce jour ; `[ouverture, fermeture]` sinon.
 */
export function effectiveDayHours(magasin, dow, ymd, defaults = OPENING_HOURS) {
  const has = (o, k) => o && Object.prototype.hasOwnProperty.call(o, k);
  if (ymd && has(magasin && magasin.horaires_exceptions, ymd)) return magasin.horaires_exceptions[ymd];
  if (has(magasin && magasin.horaires, dow)) return magasin.horaires[dow];
  return defaults[dow];
}

/**
 * Caisse ouverte APRÈS l'heure de fermeture (ou un jour fermé), d'après les horaires EFFECTIFS du jour.
 * @param {{sec:number}} nowCayenne
 * @param {[number,number]|null} dayHours  horaires effectifs du jour ([ouverture, fermeture] ou null)
 * @returns {{past:boolean, closeHour:(number|null)}}
 */
export function pastClosing(nowCayenne, dayHours) {
  if (!dayHours) return { past: true, closeHour: null };    // jour fermé (défaut, override ou exception)
  return { past: nowCayenne.sec >= dayHours[1] * 3600, closeHour: dayHours[1] };
}

export function readSnoozeUntil(code) {
  try { return Number(localStorage.getItem(SNOOZE_PREFIX + code)) || 0; } catch { return 0; }
}
export function writeSnoozeUntil(code, untilMs) {
  try { localStorage.setItem(SNOOZE_PREFIX + code, String(untilMs)); } catch { /* stockage indispo -> report en mémoire seulement */ }
}
export function clearSnooze(code) {
  try { localStorage.removeItem(SNOOZE_PREFIX + code); } catch { /* noop */ }
}

/**
 * Doit-on afficher le rappel pour ce magasin ?
 * Ouverte + après l'heure de fermeture (ou jour fermé) + report expiré.
 */
export function shouldRemind({ isOpen, code, nowMs, nowCayenne, dayHours }) {
  if (!isOpen || !code) return false;
  if (!pastClosing(nowCayenne, dayHours).past) return false;
  return nowMs >= readSnoozeUntil(code);
}
